/**
 * IntroductionPage
 *
 * Hero-style intro with product benefits and gated Get Started flow that
 * redirects basic users to upgrade before continuing.
 */
import React from 'react';
import { Video, Send, FileText, PlayCircle } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';

export function IntroductionPageButtons({ setIsVideoModalOpen, onNext }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user has premium subscription
  const isPremium = user?.planLevel === 'premium' || user?.subscriptionStatus?.currentPlanLevel === 'premium';
  
  const handleGetStarted = () => {
    if (!isPremium) {
      // Redirect to pricing page with return path
      navigate('/app/account', { 
        state: { 
          fromFeature: true,
          returnPath: '/app/campaigns/new',
          featureName: 'Campaign Creation'
        } 
      });
      return;
    }
    
    // If premium, proceed to next step
    onNext();
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-end">
      <button
        type="button"
        onClick={() => setIsVideoModalOpen(true)}
        className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
      >
        <PlayCircle className="h-4 w-4" />
        Watch Demo
      </button>
      <button
        type="button"
        onClick={handleGetStarted}
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600"
      >
        Get Started
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export function IntroductionPage({ onNext }) {
  return (
    <div className="max-w-3xl">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Supporter Videos, Collected With Ease</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Turn authentic stories from your community into polished, shareable content with our professional video templates that stand out on social media.
        </p>
      </div>

      <div className="space-y-5">
        <div className="p-5 bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800">
          <div className="flex gap-4">
            <div className="bg-primary-50 dark:bg-primary-900/20 p-3 rounded-lg h-10 w-10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Video className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 dark:text-white">Create Your Campaign</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Design your campaign with custom questions, branded elements, and personalized themes. Set up the perfect foundation for collecting impactful supporter stories.
              </p>
            </div>
          </div>
        </div>

        <div className="p-5 bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800">
          <div className="flex gap-4">
            <div className="bg-primary-50 dark:bg-primary-900/20 p-3 rounded-lg h-10 w-10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Send className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 dark:text-white">Share With Your Community</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Send your campaign link to customers, supporters, or team members. They can easily record high-quality videos directly from any device without downloads.
              </p>
            </div>
          </div>
        </div>

        <div className="p-5 bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800">
          <div className="flex gap-4">
            <div className="bg-primary-50 dark:bg-primary-900/20 p-3 rounded-lg h-10 w-10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 dark:text-white">Manage & Review Responses</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Review submitted videos, moderate content, and organize responses. Download individual videos and manage your collection of authentic supporter stories.
              </p>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
} 